import { REFUND_POLICY_URL, countReadySessions } from './bookingHelpers';

const CheckIcon = (props) => (
  <svg {...props} className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const BookSessionPricingSummary = ({
  bookingStep,
  selectedSessions,
  sessionsByLearner,
  sessionMaterials,
  sessionsSubtotal,
  serviceFees,
  appliedDiscount,
  useCredits,
  availableCredits,
  creditsToUse,
  bookingTotal,
  selectedLearningHours,
  isBooking,
  bookingError,
  onPayNow,
  onAddToCart,
  onCancelAll,
  onReviewBooking,
}) => {
  const showPayActions = bookingStep >= 2;
  const total = Math.max(bookingTotal - appliedDiscount - creditsToUse, 0);
  const learnerCount = new Set(selectedSessions.map((session) => session.learnerLabel)).size;

  return (
    <div className="space-y-6">
      {bookingStep === 2 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900">Upload Progress Summary</h3>
          <div className="mt-4 space-y-2 text-sm">
            {sessionsByLearner.map((group) => {
              const ready = countReadySessions(group.sessions, sessionMaterials);
              return (
                <div key={group.label} className="flex justify-between text-gray-700">
                  <span>{group.label}</span>
                  <span className="font-medium">
                    {group.sessions.length} Sessions · {ready} Completed
                  </span>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-sm text-gray-600">
            Total Materials Uploaded:{' '}
            <span className="font-semibold text-brand-dark-blue">
              {countReadySessions(selectedSessions, sessionMaterials)} Session
              {countReadySessions(selectedSessions, sessionMaterials) !== 1 ? 's' : ''} Ready
            </span>
          </p>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-6">
        {bookingError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {bookingError}
          </div>
        )}
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Pricing Summary</h3>

        <div className="mb-4 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Session Cost</span>
            <span className="text-gray-900">${sessionsSubtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Service Fees</span>
            <span className="text-gray-900">${serviceFees.toFixed(2)}</span>
          </div>
          {appliedDiscount > 0 && (
            <div className="flex justify-between text-green-600">
              <span className="font-medium">Discount</span>
              <span className="font-medium">-${appliedDiscount.toFixed(2)}</span>
            </div>
          )}
          {useCredits && availableCredits > 0 && (
            <div className="flex justify-between text-brand-medium-blue">
              <span className="font-medium">Credits applied</span>
              <span className="font-medium">-${creditsToUse.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 font-semibold text-gray-900">
              Total
              {showPayActions && (
                <a
                  href={REFUND_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-medium-blue text-xs font-bold text-white hover:bg-brand-dark-blue"
                  title="View Refund Policy"
                  aria-label="View Refund Policy"
                >
                  ?
                </a>
              )}
            </span>
            <span className="font-semibold text-gray-900">${total.toFixed(2)}</span>
          </div>
        </div>

        <div className="mt-4 space-y-2 rounded-lg bg-blue-50 p-3 text-sm">
          <div className="flex justify-between text-gray-700">
            <span>Total Learning Time</span>
            <span className="font-semibold">{selectedLearningHours.toFixed(1)} Hours</span>
          </div>
          <div className="flex justify-between text-gray-700">
            <span>Selections</span>
            <span className="font-semibold">
              {selectedSessions.length} Sessions | {learnerCount} Learner{learnerCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {showPayActions ? (
          <>
            <p className="mt-4 flex items-start gap-2 text-xs text-gray-600">
              <CheckIcon className="mt-0.5 shrink-0 text-green-600" />
              You may reschedule or cancel up to 24 hours before the session.
            </p>
            <button
              type="button"
              onClick={onPayNow}
              disabled={isBooking || selectedSessions.length === 0}
              className="mt-6 w-full rounded-lg bg-green-600 py-3 px-6 font-semibold text-white shadow-lg transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {isBooking ? 'Submitting...' : 'PAY NOW'}
            </button>
            <button
              type="button"
              onClick={onAddToCart}
              className="mt-3 w-full rounded-lg border-2 border-brand-gold bg-amber-400 py-3 px-6 font-semibold text-brand-dark-blue shadow transition hover:bg-amber-300"
            >
              ADD TO CART
            </button>
            <button
              type="button"
              onClick={onCancelAll}
              className="mt-4 w-full text-sm font-semibold text-gray-500 hover:text-gray-700"
            >
              CANCEL ALL SELECTIONS
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onReviewBooking}
              disabled={selectedSessions.length === 0}
              className="mt-6 w-full rounded-lg bg-brand-medium-blue px-6 py-3 font-semibold text-white shadow-lg transition hover:bg-brand-dark-blue disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              Continue to Upload
            </button>
            <p className="mt-4 text-center text-xs text-gray-500">
              You won&apos;t be charged until the tutor confirms the session
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default BookSessionPricingSummary;
